import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserProfileService } from '../../_services/user-profile.service';
import { AuthenticationService } from '../../_services/authentication.service';
import { UserProfile, Companies } from '../../_models/user-profile';
import { Error } from '../../_models/messages';
import { NgxPermissionsService } from 'ngx-permissions';
import { NavLinkAvailabilityService } from 'src/app/_services/nav-link-availability.service';
import { PortalApps } from 'src/app/_models/portal-routes';
import { Title } from '@angular/platform-browser';
import { ConfigurationService } from '../../_services/configuration.service';

@Component({
  selector: 'app-company-select',
  templateUrl: './company-select.component.html',
  styleUrls: ['../login/login.component.css']
})
export class CompanySelectComponent implements OnInit, OnDestroy {
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  showDashboards: boolean = true;
  userProfile: UserProfile;
  companySelectForm: UntypedFormGroup
  companyOptions: Companies[] = [];
  controlPanelAccess: Companies[] = [];
  infoClass: string = "alert alert-info";
  infoMessage: string = "";
  error: Error = new Error;
  availableLinks: PortalApps[];
  requestedReportId: string = '';
  redirectToReport: boolean = false;
  reportServerUrl: string = '';
  // Subjects
  private onDestroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private userService: UserProfileService,
    private configurationService: ConfigurationService,
    private permissionsService: NgxPermissionsService,
    private navLinkService: NavLinkAvailabilityService,
    private titleService: Title
  ) {
    this.userService.userProfile$.pipe(takeUntil(this.onDestroy$)).subscribe(
      userProfile => { this.userProfile = userProfile });

    this.navLinkService.availableLinks$.pipe(takeUntil(this.onDestroy$)).subscribe(
      availableLinks => { this.availableLinks = availableLinks });

    this.companySelectForm = this.fb.group({
      companySelect: ['', Validators.required]
    });

    this.showDashboards = this.configurationService.config.showDashboards;
    this.reportServerUrl = this.configurationService.config.reportViewerAddress;
    this.configurationService.configData$.pipe(takeUntil(this.onDestroy$)).subscribe(
      configData => {
        this.reportServerUrl = configData.reportViewerAddress;
        this.showDashboards = configData.showDashboards;
      }
    );
  }

  ngOnInit(): void {
    this.titleService.setTitle('Company Select - Productioneer Portal');
    this.isLoading = true;

    if (this.authenticationService.isAuthenticated()) {
      this.route.queryParams.subscribe((params: Params) => {
        if (params['fromLogin']) {
          this.getCompanyOptionsFromApi(true);
          if (params['reportid']) {
            this.requestedReportId = params['reportid'];
            this.redirectToReport = true;
          }
        }
        else {
          // Routed from Change Companies link
          this.getCompanyOptionsFromApi(false);
        }
      })
    }
    else {
      this.router.navigate(['login']);
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  getCompanyOptionsFromApi(fromLogin: boolean) {
    this.isLoading = true;
    this.companyOptions = [];
    this.controlPanelAccess = [];

    this.authenticationService.getCompanyAccessAndPermissionsForUser().subscribe(
      (data: Companies[]) => {
        data.forEach(company => {
          if (company.hasPortalAccess == true) {
            this.companyOptions.push(company);
          }
          if (company.hasControlPanelAccess == true) {
            this.controlPanelAccess.push(company);
          }
        })
        if (this.companyOptions.length && (this.companyOptions.length > 0)) {
          this.checkIfSingleCompanyAvailable(fromLogin);
        }
        else {
          this.error.isPresent = true;
          this.error.message = "There appears to be an issue with your account. Please contact support@mi4.com to resolve this issue.";
          this.isLoading = false;
        }
      },
      err => {
        console.log(err);
        this.error.isPresent = true;
        this.error.message = err.error || "An error occurred. Please refresh the page. If this error continues, contact support@mi4.com."
        this.isLoading = false;
      }
    )
  }

  checkIfSingleCompanyAvailable(fromLogin: boolean) {
    if (this.companyOptions.length && (this.companyOptions.length == 1)) {
      if (fromLogin) {
        this.loadCompanyAndNavigate(0);
      }
      else {
        this.isLoading = false;
      }
    }
    else {
      this.isLoading = false;
    }
  }

  onSelect() {
    this.isSubmitting = true;
    const selectedIndex = JSON.parse(this.companySelectForm.value.companySelect);
    this.loadCompanyAndNavigate(selectedIndex);
  }

  loadCompanyAndNavigate(index: number) {
    this.error.isPresent = false;
    this.error.message = null;

    if (index > -1) {
      let permissions: string[] = [];
      let selectedCompany: string = this.companyOptions[index].companyName;

      this.userProfile.currentCompany = selectedCompany;
      this.userProfile.id = this.companyOptions[index].companyUserID;
      this.userProfile.name = this.companyOptions[index].username;
      for (var i = 0; i < this.companyOptions[index].permissions.length; i++) {
        permissions.push(this.companyOptions[index].permissions[i]);
      }

      if (this.userProfile.isSuperAdmin) {
        permissions.push("SUPERADMIN");
      }
      else {
        for (var i = 0; i < this.controlPanelAccess.length; i++) {
          if (this.controlPanelAccess[i].companyName == selectedCompany) {
            permissions.push("CONTROLPANELADMIN");
            break;
          }
        }
      }

      this.userProfile.permissions = permissions;
      this.permissionsService.loadPermissions(permissions);
      this.userService.updateProfile(this.userProfile);

      this.availableLinks.forEach(app => {
        if (app.application == 'controlPanel') {
          app.enabled = false;
        }
        if (app.application == 'portal') {
          app.routes.forEach(route => {
            if (route.name == 'dashboards') {
              route.enabled = this.showDashboards;
            }
          })
        }
      })

      if (permissions.length > 0) {
        if (permissions.includes("SUPERADMIN") || permissions.includes("CONTROLPANELADMIN")) {
          this.availableLinks.forEach(app => {
            if (app.application == 'controlPanel') {
              app.enabled = true;
              app.routes.forEach(route => {
                if (route.name == 'users') {
                  route.enabled = true;
                }
                if (route.name == 'wells') {
                  route.enabled = true;
                }
              })
            }
          })
        } 
      }

      this.navLinkService.updateAvailableLinks(this.availableLinks);
      if (this.redirectToReport) {
        let reportAddressUrl = this.reportServerUrl + 'Default?reportid=' + this.requestedReportId;
        window.open(reportAddressUrl, '_blank');
      }
      if (this.authenticationService.redirectUrl) {
        console.log('Redirect to: ' + this.authenticationService.redirectUrl);
        this.router.navigateByUrl(this.authenticationService.redirectUrl);
        this.authenticationService.redirectUrl = null;
      }
      else {
        this.router.navigate(['app/home']);
      }
      this.isSubmitting = false;
    }
    else {
      this.error.isPresent = true;
      this.error.message = "There appears to be an issue with your account. Please contact support@mi4.com to resolve this issue.";
      this.isSubmitting = false;
    }
  }
}
